import { css } from '@emotion/react';
import {
  CardAffinity,
  CardType,
  getCardAffinityString,
  getCardTypeString,
  isCardAffinity,
  isCardType,
} from '@mythos/game/CardTypes';
import {
  AGES_PER_GAME,
  MAX_GOLD,
  MAX_GOLD_PER_AGE,
  MAX_MILITARY,
  TURNS_PER_AGE,
} from '@mythos/game/GameConstants';
import { getDefaultGameOptions } from '@mythos/game/GameOptions';
import { range, sample } from 'lodash';
import React, { ReactNode, useEffect } from 'react';
import CardView, { BasicCardRenderer } from './CardView';
import { UIColorPalette } from './ColorPalette';
import HoverCardStore from './HoverCardStore';
import Symbols, {
  CardAffinityToSymbol,
  CardTypeToSymbol,
  SymbolView,
} from './Symbols';
import TextWithSymbols from './TextWithSymbols';
import { BasicTributeRenderer, TRIBUTE_TYPE_TO_IMAGE } from './TributeCardView';

import BaseBoardExample from '../assets/BaseBoardExample.svg';
import BidderExample from '../assets/BidderExample.svg';
import DiceExample from '../assets/DiceExample.svg';
import Favor100Example from '../assets/Favor100Example.svg';
import FavorTrackerExample from '../assets/FavorTrackerExample.svg';
import GoldTokenExample from '../assets/GoldTokenExample.svg';
import MilitaryTokenExample from '../assets/MilitaryTokenExample.svg';
import RulesCardAnatomy from '../assets/RulesCardAnatomy.svg';
import TributeCardAnatomy from '../assets/RulesTributeCardAnatomy.svg';

import { probabilityWinningMultiBattle } from '@mythos/game/Probability';
import { endOfGamePointsPerToken } from '@mythos/game/Rules';
import invariant from 'invariant';
import { MEDIA_QUERY_MOBILE } from 'mobile_ui/MStyles';
import { match } from 'ts-pattern';
import { useShallow } from 'zustand/shallow';
import BaseBoardFilledExample from '../assets/BaseBoardFilledExample.svg';
import ConflictBidderExample from '../assets/ConflictBidderExample.svg';
import GameBoardAnatomy from '../assets/GameBoardAnatomy.jpg';
import PlayerBoardExample from '../assets/PlayerBoardExample.jpg';
import { CardBackType, CardBackView } from './CardBackView';
import { useGameDefinitionsStore } from './stores/gameDefinitionsStore';
import { useSessionStore } from './stores/sessionStore';

function RulesPage() {
  const session = useSessionStore((state) => state.session);
  const { gameDefinitions, fetchGameDefinitions } = useGameDefinitionsStore(
    useShallow((state) => ({
      gameDefinitions: state.gameDefinitions,
      fetchGameDefinitions: state.fetchGameDefinitions,
    })),
  );

  useEffect(() => {
    fetchGameDefinitions();
  }, [fetchGameDefinitions]);

  useEffect(() => {
    if (gameDefinitions) {
      // scroll to hash
      const hash = window.location.hash;
      if (hash) {
        const element = document.getElementById(hash.slice(1));
        if (element) {
          element.scrollIntoView();
        }
      }
    }
  }, [gameDefinitions]);

  if (!gameDefinitions) {
    return <div className="loading">Loading...</div>;
  }
  const cards = gameDefinitions.cardDefs;
  const tributeCards = gameDefinitions.tributeCardDefs;

  // Overview section
  const overview_section = (
    <PageSection title="Overview" linkName="overview">
      <p css={RulesPageStyles.section_paragraph}>
        In a distant land of fiercely warring city-states, many seek the favor
        of the gods to survive. Compete with other players to gather land,
        develop your city into a booming polis, and appease the gods so that
        they may bless your mortal existence. In time, your victories may carve
        your story into the legends of Mythos.
      </p>
      <p css={RulesPageStyles.section_paragraph}>
        The game is divided into {AGES_PER_GAME} ages of {TURNS_PER_AGE} turns.
        During a turn, players will plan drafts and mobilize their military to
        grow their empire. As they do, they will curry Favor with the Gods by
        completing Tributes. At the end of the game, the player who has gathered
        the most Favor wins the game.
      </p>
      <p css={RulesPageStyles.section_paragraph}>
        Mythos is a card-drafting game based around simultaneous actions.
        Players should hide their actions for a given phase of the game until
        all players have made their decisions. Once everyone is ready, players
        reveal their decisions and take the appropriate actions.
      </p>
    </PageSection>
  );

  // Resources section
  const resources = [
    {
      name: 'gold',
      symbol: Symbols.GOLD,
      description: `Gold is used to obtain cards.`,
    },
    {
      name: 'military',
      symbol: Symbols.MILITARY,
      description: `Military helps you compete for cards by adding to your dice rolls.`,
    },
    {
      name: 'favor',
      symbol: Symbols.FAVOR,
      description:
        'Favor is gained by obtaining cards and completing Tributes. At the end of the game, the player with the most $FAVOR wins.',
    },
    {
      name: 'towns',
      symbol: Symbols.BASE_GOLD,
      description:
        'Towns reduce the amount of $GOLD you need to pay when obtaining cards by 1 $GOLD per $TOWN.',
    },
    {
      name: 'forts',
      symbol: Symbols.BASE_MILITARY,
      description:
        'Forts reduce the amount of $MILITARY you need to pay when bidding by 1 $MILITARY per $FORT.',
    },
  ];
  const resources_section = (
    <PageSection title="Resources" linkName="resources">
      <ul css={RulesPageStyles.list}>
        {resources.map((resource) => (
          <li key={resource.name} css={RulesPageStyles.listItemSymbolBullet}>
            <SymbolView symbol={resource.symbol} css={RulesPageStyles.symbol} />
            <TextWithSymbols
              content={resource.description}
              symbolStyle={RulesPageStyles.textSymbol}
            />
          </li>
        ))}
        <TextWithSymbols
          content={`Players can only have up to a limited amount of $GOLD and $MILITARY at any given time, based on the Age. These limits are ${MAX_GOLD_PER_AGE[0]} for Age I, ${MAX_GOLD_PER_AGE[1]} for Age II, and ${MAX_GOLD_PER_AGE[2]} for Age III. Any time a player would gain more than the maximum, they instead gain nothing and the excess is lost.`}
          symbolStyle={RulesPageStyles.textSymbol}
        />
      </ul>
    </PageSection>
  );

  // Card Outputs section
  type CardOutputSection = {
    name?: string;
    exampleDefID: string;
    description: string;
  };
  const card_outputs: CardOutputSection[] = [
    {
      name: 'Building Effects',
      exampleDefID: 'farm',
      description:
        'grant $TOWN and $FORT, which passively grant discounts on the $GOLD and $MILITARY you spend. So, if you purchase a card that costs 5 $GOLD and have 2 $TOWN, you only need to spend 3 $GOLD to purchase it. When bidding, if you bid 6 and have 1 $FORT, you only need to spend 5 $MILITARY. This grants 1 $TOWN.',
    },
    {
      name: 'Unit Effects',
      exampleDefID: 'warrior',
      description:
        'grant $GOLD and $MILITARY during the Unit phase of each turn. This effect grants 1 $MILITARY during each Unit phase.',
    },
    {
      name: 'Obtain Effects',
      exampleDefID: 'new_roads',
      description:
        'grant tokens when the card is placed onto your board. This effect grants 5 $GOLD and 5 $MILITARY upon obtaining the card.',
    },
    {
      name: 'Upgrade Effects',
      exampleDefID: 'soldier_of_tyros',
      description:
        'grant additional effects if a certain criteria is met. This Upgrade effect grants 1 $MILITARY during each Unit Phase, plus an additional one if the player owns 3 $TYROS cards (including itself).',
    },
    // {
    //   output: [
    //     {
    //       ability: CardAbilities.EachTurnAbility,
    //       params: 'M/2D',
    //     },
    //   ],
    //   description:
    //     'Gain 1 Military token for every two buildings you own at the start of each turn.',
    // },
    {
      name: 'Synergy Obtain Effects',
      exampleDefID: 'citadel',
      description:
        'grant scaling effects based on cards the player owns. The effect does not include the obtained card itself. This effect grants 1 $MILITARY upon obtaining for every other $BUILDING card the player already owns.',
    },
    {
      name: 'Prophecy Effects',
      exampleDefID: 'piety',
      description:
        'grant $FAVOR at the end of the game based on the cards you own. This Prophecy effect grants 1 $FAVOR for every $ZEONA card you own at the end of the game. These effects include themselves, if applicable.',
    },

    // TODO: re-introduce in modules section
    // {
    //   output: [
    //     {
    //       ability: CardAbilities.GainOtherCardAbility,
    //       params: '+T=V',
    //     },
    //   ],
    //   description: 'Gain 1 Favor every time you complete a Tribute.',
    // },
    // {
    //   output: [
    //     {
    //       ability: CardAbilities.GainOtherCardAbility,
    //       params: '+T=G M V',
    //     },
    //   ],
    //   description:
    //     'Gain 1 Favor, Gold and Military every time you complete a Tribute.',
    // },
  ];

  const card_output_divs = card_outputs.map((card_output, i) => {
    let card = cards.find((card) => card.defID === card_output.exampleDefID);
    if (!card) {
      console.error('Could not find card: ' + card_output.exampleDefID);
      return;
    }
    return (
      <span key={i} css={RulesPageStyles.section_card_outputs_container}>
        <div className="card-with-note">
          {BasicCardRenderer({
            card,
            onCardEnter: (card) =>
              HoverCardStore.setCard(card, BasicCardRenderer),
            onCardLeave: () => HoverCardStore.setCard(null, null),
          })}
        </div>
        <span css={RulesPageStyles.section_paragraph}>
          <span css={RulesPageStyles.subsection_title}>{card_output.name}</span>{' '}
          <TextWithSymbols
            content={card_output.description}
            symbolStyle={RulesPageStyles.textSymbol}
          />
        </span>
      </span>
    );
  });
  const card_outputs_section = (
    <PageSection title="Card Outputs" linkName="card_outputs">
      Cards will generally grant an output represented by symbols on the card.
      Here are some example cards and what their abilities do:
      {card_output_divs}
    </PageSection>
  );

  // Gods section
  const gods = [
    {
      name: 'Zeona',
      image: TRIBUTE_TYPE_TO_IMAGE['Zeona'],
      description:
        "Zeona, the Goddess of Order, sits at the helm of the pantheon of Mythos. Zeona is said to keep the other Gods in check and controlling the balance of power in Mythos. She is often depicted as a fierce, yet graceful lioness wielding a hammer. In the days before the city-states, humans were barbaric and chaotic. Zeona blessed humans with ideals of order and structure, and from this, the polis was born. Zeona's worshippers often create decadent temples in her name, and pray for wealth, comfort, and protection against the otherwise chaotic world.",
    },
    {
      name: 'Tyros',
      image: TRIBUTE_TYPE_TO_IMAGE['Tyros'],
      description:
        'Tyros, the God of Conflict, loves nothing more to witness the struggle and bloodshed of mortals as they combat each other. Tyros is often depicted as a menacing bull-centaur brandishing a spear. Worshippers of Tyros give little regard for their own life, as to die in battle is to die in the glory of Tyros. As such, they are fierce and relentless, and are regarded as some of the strongest fighters in the world.',
    },
    {
      name: 'Derion',
      image: TRIBUTE_TYPE_TO_IMAGE['Derion'],
      description:
        "Derion, the God of Plenty, bestows upon the world great harvest and bounty. Derion is Tyros' brother, and often admonishes his aggression. He favors those who labor in the fields, and rewards their hard work with bountiful output. Derion is oftentimes depicted as a great ox-centaur, but many say he takes human form during the harvest moon to work alongside people. Worshippers of Derion are often in tune with nature, preferring the fields of a farm to the city bustle.",
    },
  ];
  const gods_section = (
    <PageSection title="Lore of the Game: The Gods" linkName="lore">
      {gods.map((god) => {
        return (
          <PageSection key={god.name} title={god.name}>
            <div
              css={[
                RulesPageStyles.section_with_image,
                RulesPageStyles.section_with_image_mirrored,
                RulesPageStyles.section_with_image_narrow,
              ]}
            >
              <img src={god.image} css={RulesPageStyles.image} />
              <p css={RulesPageStyles.section_paragraph}>{god.description}</p>
            </div>
          </PageSection>
        );
      })}
    </PageSection>
  );

  type PhaseSection = {
    name: string;
    description: ReactNode;
    exampleImage?: string;
    exampleCaption?: string;
  };
  // Core rules section
  const phases: PhaseSection[] = [
    {
      name: 'Setup',
      description: (
        <TextWithSymbols
          content="Cards are drawn from the current age's deck and dealt to the Trade Row, right to left, until the Trade Row has cards equal to the number of players plus one. Tribute Cards are dealt to the Tribute Row until the Tribute Row has 3 cards. If the Tribute Deck is ever empty, the discarded Tributes are reshuffled into the Tribute Deck."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      ),
    },
    {
      name: 'Unit',
      description: (
        <TextWithSymbols
          content="Each player gains $MILITARY and $GOLD from their card Unit effects."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      ),
      // exampleImage: PlayerBoardExample,
      // exampleCaption:
      //   'Example: The player gains 1 Gold and 2 Military. The Mill and Worship cards do not contribute to production.',
    },
    {
      name: 'Planning',
      description: (
        <TextWithSymbols
          content="During this phase, each player selects a Trade Row Card and a number of $MILITARY to bid. Both of these choices are made in secret. A player may bid up to their current $MILITARY count plus the number of $FORT they have. Since $FORT discount the amount of $MILITARY spent, a player's minimum bid will be set to the number of $FORT they have."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      ),
      exampleImage: BidderExample,
      exampleCaption:
        'Example: The purple player has selected the second card in the Trade Row and bid 3 Military.',
    },
    {
      name: 'War',
      description: (
        <TextWithSymbols
          content="Players' Trade Row selections and bids are revealed. Players spend $MILITARY equal to their bid minus the number of $FORT they have. If multiple players draft the same card, they are in a clash over the card.\n\nEach player in a clash rolls a number of six-sided dice equal to the age, and adds their bid. The winner is the player with the final highest total.\n\nIf two or more players tie for the highest total, those players reroll until only one player has the highest total. Other players within the clash are eliminated from contention."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      ),
      exampleImage: ConflictBidderExample,
      exampleCaption:
        'Example: The purple and green players have selected the same card. The purple player bid 3 Military and rolled a 2 for a total of 5, while the green player bid 1 Military and rolled a 3, for a total of 4. The purple player has defeated the green player.',
    },
    {
      name: 'Resolution',
      description: (
        <TextWithSymbols
          content="For each drafted card, the winner of the clash over that card can now obtain the card. If the card was drafted by only one player, then that player may obtain the card. To obtain a card, the player spends $GOLD equal to the cost of the card minus the number of $TOWN they have.\n\nIf the winning player cannot afford the card, or they do not wish to obtain it, they must take a Basic card. Players who lost a clash must take a Basic card.\n\nUpon obtaining a card, players gain the resources listed on the card's Obtain Effects."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      ),
    },
    {
      name: 'Tributes',
      description: (
        <TextWithSymbols
          content="If a player have met the Completion Condition for a Tribute, taking into account the results of their actions this turn, they gain the amount of Tribute Tokens listed on that Tribute.\n\nIf multiple players have completed a Tribute, all of those players gain the Tribute Tokens."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      ),
    },
    {
      name: 'Cleanup',
      description: (
        <TextWithSymbols
          content="Remove all completed Tributes from the Tribute Row. Slide all remaining cards on the Trade Row to the right until they occupy the highest-numbered slots for the number of players in the game. In a two player game, instead discard all cards in the Trade Row.\n\nIf it is the last turn of the Age, proceed to the end of the age. Otherwise, advance the Turn Tracker."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      ),
    },
  ];
  const phase_divs = phases.map((phase) => {
    return (
      <p key={phase.name} css={RulesPageStyles.section_paragraph}>
        <span css={RulesPageStyles.subsection_title}>{phase.name}</span>:{' '}
        {phase.description} <br />
        {phase.exampleImage && (
          <img
            src={phase.exampleImage}
            css={RulesPageStyles.imagePhaseExample}
          />
        )}
        <span css={RulesPageStyles.imageCaption}>{phase.exampleCaption}</span>
      </p>
    );
  });
  const eogPoints = endOfGamePointsPerToken(getDefaultGameOptions());
  const core_rules_section = (
    <div>
      <PageSection title="Game Setup" linkName="setup">
        <TextWithSymbols
          content="Each player begins the game with a Leader card and gains resources from the Leader card's Obtain effect."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      </PageSection>
      <PageSection title="Start of Age" linkName="age-setup">
        <TextWithSymbols
          content="Each age has an associated deck of Trade Row cards. Shuffle the deck for the age and the Tribute deck. Place all Basic cards for the age in the corresponding Basic Piles."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      </PageSection>
      <PageSection
        title="The Turn"
        linkName="turn"
        css={css({ breakInside: 'auto' })}
      >
        The turn is divided into several phases. Only Planning and Resolution
        require player input:
        {phase_divs}
      </PageSection>
      <PageSection title="End of Age" linkName="age-end">
        <TextWithSymbols
          content="Remove all cards from the Trade Row, Tribute Row, and Basic Piles. Remove the Trade Row Deck from the board. Players keep all of their cards and resources. Advance to the next age."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      </PageSection>
      {/* <PageSection title="Game End" linkName="game-end">
        At the end of the game, players will score favor based on their
        remaining resources and Prophecy Effects. 1 Favor is awarded for every{' '}
        {eogPoints.gold} Gold and every {eogPoints.military} Military. Calculate
        Favor gained from Prophecy Effects and add that to the final Favor
        total. The player with the most favor wins the game.
      </PageSection> */}
      <PageSection title="Game End" linkName="game-end">
        <TextWithSymbols
          css={RulesPageStyles.section_paragraph}
          content={`At the end of the game, players will total their $FAVOR based on their Base Favor listed on cards, $FAVOR from Prophecy Effects, and $FAVOR from Tribute Tokens. The player with the most $FAVOR wins the game.`}
          symbolStyle={RulesPageStyles.textSymbol}
        />
      </PageSection>
      {/* TODO: Example of end of game scoring */}
    </div>
  );

  type CardTypeDescription = {
    type: CardAffinity | CardType;
    description: string;
    exampleDefID: string;
  };
  // Card types section
  const card_affinities: CardTypeDescription[] = [
    {
      type: CardAffinity.Derion,
      description:
        ' cards primarily grant $GOLD and $TOWN. $DERION cards are green.',
      exampleDefID: 'marketplace',
    },
    {
      type: CardAffinity.Tyros,
      description:
        ' cards primarily grant $MILITARY and $FORT. $TYROS cards are red.',
      exampleDefID: 'warrior',
    },
    {
      type: CardAffinity.Zeona,
      description: ' cards primarily grant $FAVOR. $ZEONA cards are blue.',
      exampleDefID: 'divine_blessing',
    },
  ];
  const card_types: CardTypeDescription[] = [
    {
      type: CardType.Building,
      description:
        ' cards grant Building Effects. These effects passively grant discounts on the $GOLD and $MILITARY you spend.',
      exampleDefID: 'farm',
    },
    {
      type: CardType.Unit,
      description:
        ' cards grant Unit Effects. These effects grant you $GOLD and $MILITARY during the Unit Phase of every turn.',
      exampleDefID: 'hoplite',
    },
    {
      type: CardType.Event,
      description:
        ' cards grant $GOLD and $MILITARY, but only through Obtain Effects. They do not have any Unit or Building effects.',
      exampleDefID: 'heroes_feast',
    },
  ];
  const other_cards: CardTypeDescription[] = [
    {
      type: CardAffinity.Leader,
      description:
        ' cards are the cards each player starts with at the beginning of the game. They determine what resources a player begins with, as well as their initial Unit effects. $LEADER cards are gray.',
      exampleDefID: 'leader',
    },
    {
      type: CardAffinity.Basic,
      description:
        ' cards are not placed on the Trade Row. Instead, players take a $BASIC card whenever they lose a war or do not wish to obtain the card they drafted. Each $BASIC card type has different effects, which are explained below. $BASIC cards are brown.',
      exampleDefID: 'scavenge_i',
    },
  ];
  const cardTypeDescriptionDiv = function (
    card_type_descriptions: CardTypeDescription,
  ) {
    const { is_type, symbol, card_type_name } = match(
      card_type_descriptions.type,
    )
      .when(
        (x) => isCardAffinity(x),
        (x) => {
          return {
            is_type: false,
            symbol: CardAffinityToSymbol[x],
            card_type_name: getCardAffinityString(x),
          };
        },
      )
      .when(
        (x) => isCardType(x),
        (x) => {
          return {
            is_type: true,
            symbol: CardTypeToSymbol[x],
            card_type_name: getCardTypeString(x),
          };
        },
      )
      .otherwise((x) => invariant(false, 'unknown card affinity/type: %s', x));
    let card = cards.find(
      (card) => card.defID === card_type_descriptions.exampleDefID,
    );
    if (!card) {
      console.error(
        'Could not find card: ' + card_type_descriptions.exampleDefID,
      );
      card = sample(
        cards.filter((card) => {
          if (is_type) {
            return card.type === card_type_descriptions.type;
          }
          return card.affinity === card_type_descriptions.type;
        }),
      )!;
    }
    return (
      <div
        key={card_type_descriptions.type}
        css={RulesPageStyles.section_card_types}
      >
        <p>
          <span css={RulesPageStyles.subsection_title}>
            <SymbolView symbol={symbol} css={RulesPageStyles.symbol_inline} />
            {card_type_name}
          </span>
          <TextWithSymbols
            content={card_type_descriptions.description}
            symbolStyle={RulesPageStyles.textSymbol}
          />
        </p>
        <div css={RulesPageStyles.card_with_note}>
          <CardView
            card={card}
            onCardEnter={(card) =>
              HoverCardStore.setCard(card, BasicCardRenderer)
            }
            onCardLeave={() => HoverCardStore.setCard(null, null)}
          />
        </div>
      </div>
    );
  };
  type BasicCardDescription = {
    name: string;
    description: string;
    cardDefIDs: string[];
  };
  const basicCardDescriptions: BasicCardDescription[] = [
    {
      name: 'Scavenge',
      description:
        'On Obtain, Scavenge grants you $GOLD and $MILITARY, increasing with every age.',
      cardDefIDs: ['scavenge_i', 'scavenge_ii', 'scavenge_iii'],
    },
    {
      name: 'Settlement',
      description:
        'On Obtain, the Settlement grants 2 $GOLD for each other Basic card you already own. It also grants 1 $TOWN.',
      cardDefIDs: ['settlement_i'],
    },
    {
      name: 'Mercenary',
      description:
        'The Mercenary has a flexible cost. You can pay any of the listed costs, which will grant you scaling $MILITARY on Obtain. You can choose this value during the Resolution phase by first selecting the Mercenary card, then choosing an option. It also grants 1 $MILITARY each turn.',
      cardDefIDs: [
        'mercenary_1_1',
        'mercenary_1_2',
        'mercenary_1_3',
        'mercenary_1_4',
      ],
    },
  ];
  const basicCardDescriptionDiv = function (
    basic_card_descriptions: BasicCardDescription,
  ) {
    const cards = basic_card_descriptions.cardDefIDs.map((cardDefID) => {
      let card = gameDefinitions.cardDefs.find(
        (card) => card.defID === cardDefID,
      );
      invariant(card, 'Could not find basic card: ' + cardDefID);
      return card;
    });
    return (
      <div
        key={basic_card_descriptions.name + '_container'}
        css={RulesPageStyles.section_basic_cards_description_container}
      >
        <div
          key={basic_card_descriptions.name}
          css={RulesPageStyles.section_card_types}
        >
          <p>
            <span css={RulesPageStyles.subsection_title}>
              {basic_card_descriptions.name + ': '}
            </span>
            <TextWithSymbols
              content={basic_card_descriptions.description}
              symbolStyle={RulesPageStyles.textSymbol}
            />
          </p>
        </div>
        <div css={RulesPageStyles.section_basic_cards_examples_container}>
          {cards.map((card) => (
            <div key={card.name} css={RulesPageStyles.card_with_note}>
              <CardView
                card={card}
                onCardEnter={(card) =>
                  HoverCardStore.setCard(card, BasicCardRenderer)
                }
                onCardLeave={() => HoverCardStore.setCard(null, null)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };
  const card_type_divs = card_affinities.map((card_type) =>
    cardTypeDescriptionDiv(card_type),
  );
  const card_subtype_divs = card_types.map((card_type) =>
    cardTypeDescriptionDiv(card_type),
  );
  const other_card_divs = other_cards.map((card_type) =>
    cardTypeDescriptionDiv(card_type),
  );
  const basic_card_divs = basicCardDescriptions.map((basic_card) =>
    basicCardDescriptionDiv(basic_card),
  );
  const card_types_section = (
    <PageSection title="Card Deities" linkName="card_types">
      <div css={RulesPageStyles.section_card_types_container}>
        {card_type_divs}
      </div>
    </PageSection>
  );
  const card_subtypes_section = (
    <PageSection title="Card Types" linkName="card_subtypes">
      <div css={RulesPageStyles.section_card_types_container}>
        {card_subtype_divs}
      </div>
    </PageSection>
  );
  const other_cards_section = (
    <PageSection title="Other Cards" linkName="card_subtypes">
      These cards will not appear on the Trade Row, but are obtained in other
      ways. Unless otherwise noted, these cards do not have a Deity, but they do
      have a Type.
      <div css={RulesPageStyles.section_card_types_container}>
        {other_card_divs}
      </div>
    </PageSection>
  );
  const basic_cards_section = (
    <PageSection title="Basic Cards" linkName="basic_cards">
      <div css={RulesPageStyles.section_basic_cards_container}>
        {basic_card_divs}
      </div>
    </PageSection>
  );
  const set_note_section = (
    <div key="card_set" css={RulesPageStyles.section_paragraph}>
      <p>
        <span css={RulesPageStyles.subsection_title}>
          <SymbolView
            symbol={Symbols.CARD_SET}
            css={RulesPageStyles.symbol_inline}
          />
        </span>
        <TextWithSymbols
          content="Some cards or Tributes will refer to Card Sets, denoted by this symbol. This means a set of a $DERION, $TYROS, and $ZEONA card. For example, if you own 3 $DERION Cards, 3 $TYROS Cards, and 2 $ZEONA Cards, you have 2 Card Sets."
          symbolStyle={RulesPageStyles.textSymbol}
        />
      </p>
    </div>
  );

  // Anatomy section
  const card_anatomy = [
    {
      name: 'Name',
      description: 'The name of the card. This has no effect on gameplay.',
    },
    {
      name: 'Cost',
      description: 'The $GOLD cost of the card.',
    },
    {
      name: 'Favor',
      description:
        'The $FAVOR this card grants you at the end of the game. This will either be listed as a single number, known as Base Favor, or a value that scales with a Card Type or Affinity, known as a Prophecy Effect (see below). If a card does not have a Base Favor shown, it has 0 Base Favor.',
    },
    {
      name: 'Obtain Effect',
      description:
        'Some cards grant additional $GOLD or $MILITARY you obtain them.',
    },
    {
      name: 'Persistent Effect',
      description: "A card's Unit and Building Effects are listed here.",
    },
    {
      name: 'Type',
      description:
        'The type of the card. This can be $BUILDING (Building), $UNIT (Unit), or $EVENT (Event). The card type generally describes how the card primarily grants resources.',
    },
    {
      name: 'Affinity',
      description:
        'The Affinity of the card. On Trade Row Cards, this can be $DERION (Derion), $TYROS (Tyros), or $ZEONA (Zeona). $BASIC (Basic) and $LEADER (Leader) Cards also show their symbol here.',
    },
    // {
    //   name: 'Age',
    //   description: 'The age in which it appears.',
    // },
  ];
  const make_anatomy_divs = (anatomy: typeof card_anatomy) => {
    return anatomy.map((section, i) => {
      return (
        <li key={i}>
          <p css={RulesPageStyles.section_paragraph}>
            <span css={RulesPageStyles.subsection_title}>{section.name}</span>
            {section.description.length ? ': ' : ''}
            <TextWithSymbols
              content={section.description}
              symbolStyle={RulesPageStyles.textSymbol}
            />
          </p>
        </li>
      );
    });
  };
  var card_anatomy_divs = make_anatomy_divs(card_anatomy);
  const card_anatomy_card =
    cards.find((card) => card.defID === 'craftsman') || cards[0];
  const card_anatomy_section = (
    <PageSection title="Card Anatomy" linkName="card_anatomy">
      <div
        css={[
          RulesPageStyles.section_with_image,
          RulesPageStyles.section_with_image_mirrored,
        ]}
      >
        <div css={RulesPageStyles.card_anatomy_card}>
          {BasicCardRenderer({
            card: card_anatomy_card,
            overlayChildren: <img src={RulesCardAnatomy} />,
          })}
        </div>
        <ol css={RulesPageStyles.list_numbered}>{card_anatomy_divs}</ol>
      </div>
    </PageSection>
  );
  const tribute_anatomy = [
    {
      name: 'Name',
      description: 'The name of the Tribute. This has no effect on gameplay.',
    },
    {
      name: 'Constellation',
      description: `The name of the Constellation this Tribute represents. This has no effect on gameplay.`,
    },
    {
      name: 'Completion Condition',
      description:
        'How to complete the Tribute. This condition is checked during the Tribute phase at the end of each turn.',
    },
  ];
  const tribute_anatomy_divs = make_anatomy_divs(tribute_anatomy);
  const tribute_anatomy_card =
    tributeCards.find((card) => card.defID === 'establish_bounty') ||
    tributeCards[0];
  const tribute_anatomy_section = (
    <PageSection title="Tribute Anatomy" linkName="tribute_anatomy">
      <div css={RulesPageStyles.section_with_image}>
        <div css={RulesPageStyles.card_anatomy_card}>
          {BasicTributeRenderer({
            card: tribute_anatomy_card,
            overlayChildren: (
              <img src={TributeCardAnatomy} style={{ height: '100%' }} />
            ),
          })}
        </div>
        <ol css={RulesPageStyles.list_numbered} style={{ paddingLeft: 10 }}>
          {tribute_anatomy_divs}
        </ol>
      </div>
    </PageSection>
  );
  const board_anatomy = [
    {
      name: 'Trade Row Deck',
      description: 'The deck of remaining Trade Row cards for the age.',
    },
    {
      name: 'Tribute Deck',
      description: 'The deck of remaining Tribute cards.',
    },
    {
      name: 'Tribute Discard Pile',
      description: 'Completed Tributes that have been discarded.',
    },
    {
      name: 'Basic Card Piles',
      description:
        'The three Basic Card types go into the corresponding piles here.',
    },
    {
      name: 'Tribute Row',
      description: 'The active Tributes.',
    },
    {
      name: 'Trade Row',
      description:
        'The set of Trade Row cards that players can select from. The number of cards in the Trade Row is equal to the number of players plus one. Always place the cards on the Trade Row from left to right.',
    },
    {
      name: 'Turn Tracker',
      description:
        'A tracker token is placed on this area to track the current turn of the age.',
    },
    {
      name: 'Age Tracker',
      description:
        'Tracks the age of the game. Place one die per player on each space. At the start of an age, give each player their die from the space corresponding to the age. The last uncovered space is the current age.',
    },
    {
      name: 'Favor Tracker',
      description:
        'Players use tracker tokens to count their favor around the board as the game progresses.',
    },
  ];
  const board_anatomy_divs = make_anatomy_divs(board_anatomy);
  const board_anatomy_section = (
    <PageSection title="Board Anatomy" linkName="board_anatomy">
      <img src={GameBoardAnatomy} css={RulesPageStyles.imageGameBoard} />
      <ol css={RulesPageStyles.list_numbered}>{board_anatomy_divs}</ol>
    </PageSection>
  );

  const player_board_anatomy_section = (
    <PageSection title="Player Board Example" linkName="player_board">
      <img src={BaseBoardFilledExample} css={RulesPageStyles.imageGameBoard} />
      <img src={PlayerBoardExample} css={RulesPageStyles.imagePlayerBoard} />
      <TextWithSymbols
        css={RulesPageStyles.section_paragraph}
        content="Players should use their Base Boards and the area above them to track their $GOLD and $MILITARY. The Base Board is used to track the player's base $GOLD and $MILITARY. The area above the Base Board is used to track the player's current $GOLD and $MILITARY tokens."
        symbolStyle={RulesPageStyles.textSymbol}
      />
      <TextWithSymbols
        css={RulesPageStyles.section_paragraph}
        content="In the above example, the purple player has 8 $GOLD tokens and 7 base $GOLD. This means that if they purchase a card that costs 10 $GOLD, they will have to spend 3 $GOLD tokens. The player also has 13 $MILITARY tokens and 1 Base $MILITARY. This means they can bid up to 14 (13 tokens plus 1 Base $MILITARY). When in conflict, they add their bid directly to their dice roll. If they bid 14, they would only spend 13 $MILITARY tokens due to the $FORT discount."
        symbolStyle={RulesPageStyles.textSymbol}
      />
      <TextWithSymbols
        css={RulesPageStyles.section_paragraph}
        content="Players place the cards they obtain below their Base Boards. Cards should be arranged in columns based on their subtype ($UNIT, $BUILDING, or $EVENT)."
        symbolStyle={RulesPageStyles.textSymbol}
      />
    </PageSection>
  );

  const game_components_section_header = (
    <PageSection title="Game Components" linkName="components">
      {null}
    </PageSection>
  );
  type DeckItem = {
    name: string;
    description: string;
    type: CardBackType;
  };
  const decks: DeckItem[] = [
    {
      name: 'Age I',
      type: 'age1',
      description: 'The first age of the game.',
    },
    {
      name: 'Age II',
      type: 'age2',
      description: 'The second age of the game.',
    },
    {
      name: 'Age III',
      type: 'age3',
      description: 'The final age of the game.',
    },
    {
      name: 'Scavenge Cards',
      type: 'basicevent',
      description: 'The deck containing copies of the Scavenge Basic Card.',
    },
    {
      name: 'Settlement Cards',
      type: 'basicbuilding',
      description: 'The deck containing copies of the Settlement Basic Card.',
    },
    {
      name: 'Mercenary Cards',
      type: 'basicunit',
      description: 'The deck containing copies of the Mercenary Basic Card.',
    },
    {
      name: 'Tribute',
      type: 'tribute',
      description: 'The tribute deck.',
    },
  ];
  const decks_section = (
    <PageSection title="Decks" linkName="decks">
      <div css={RulesPageStyles.deck_section}>
        <div css={RulesPageStyles.decks_container}>
          {decks.map((deck, i) => (
            <div key={i} css={RulesPageStyles.deck_container}>
              <CardBackView
                card={{ id: `card-back-${deck.type}`, type: deck.type }}
              />
              {deck.name}
            </div>
          ))}
        </div>
      </div>
    </PageSection>
  );
  type GameComponent = {
    name: string;
    imageUrl: string;
    description: string;
    height?: number;
  };
  const misc_components: GameComponent[] = [
    {
      name: 'Gold Tokens',
      imageUrl: GoldTokenExample,
      description: `$GOLD is used to obtain cards. You can only have up to ${MAX_GOLD} $GOLD. There are also larger tokens to represent 5 $GOLD.`,
      height: 75,
    },
    {
      name: 'Military Tokens',
      imageUrl: MilitaryTokenExample,
      description: `$MILITARY helps you compete for cards by adding to your dice rolls. You can only have up to ${MAX_MILITARY} $MILITARY. There are also larger tokens to represent 5 $MILITARY.`,
      height: 75,
    },
    {
      name: '100 Favor Token',
      imageUrl: Favor100Example,
      description:
        'Once a player reaches 100 $FAVOR, they take this token to track their total $FAVOR.',
    },
    {
      name: 'Bidder',
      imageUrl: BidderExample,
      description:
        'Used to secretly bid on cards. The top two dials determine the amount of $MILITARY tokens the player wishes to spend, and the bottom dial determines the Trade Row index of the card they wish to draft.',
      height: 150,
    },
    {
      name: 'Dice',
      imageUrl: DiceExample,
      description:
        'Used to resolve conflicts. Players roll a number of dice equal to the age of the card they are contesting.',
    },
    {
      name: 'Favor Trackers',
      imageUrl: FavorTrackerExample,
      description: 'Used to track $FAVOR on the game board.',
    },
    {
      name: 'Base Board',
      imageUrl: BaseBoardExample,
      description:
        'Used to track Base $GOLD and $MILITARY. Whenever a player gains a card with a Base effect, they move the corresponding resource token onto the Base Board. The board has spaces for 1s and 5s tokens.',
      height: 200,
    },
  ];
  const misc_components_section = (
    <div css={RulesPageStyles.game_component_container}>
      {misc_components.map((component, i) => {
        return (
          <div key={i} css={RulesPageStyles.game_component}>
            <p css={RulesPageStyles.section_title_no_padding}>
              {component.name}
            </p>
            {/* <div css={RulesPageStyles.section_with_image}> */}
            <TextWithSymbols
              css={RulesPageStyles.section_paragraph}
              content={component.description}
              symbolStyle={RulesPageStyles.textSymbol}
            />
            <img
              src={component.imageUrl}
              css={RulesPageStyles.imageExamples}
              style={{ height: component.height }}
            />
            {/* </div> */}
          </div>
        );
      })}
    </div>
  );
  const game_components_sections = [
    game_components_section_header,
    decks_section,
    misc_components_section,
    board_anatomy_section,
    player_board_anatomy_section,
  ];
  const playing_online_section = (
    <PageSection title="Playing Online" linkName="online">
      <p css={RulesPageStyles.section_paragraph}>
        To play Mythos Online, go to{' '}
        <a href="https://mythosgame.com">https://mythosgame.com</a>. Sign in
        with your Discord account, or log in as a guest. You can join a friend's
        game lobby, or create your own.
      </p>
      <p css={RulesPageStyles.section_paragraph}>
        Mythos Online handles the game rules and resources for you. Each turn,
        you will interact during the Planning phase and Resolution phase.
      </p>
      <p css={RulesPageStyles.section_paragraph}>
        During the Planning phase, you must select a card from the Trade Row by
        left clicking on it. Cards you cannot afford are highlighted red. Your
        current selection is highlighted white. Then, set your bid by typing a
        number into the "Bid" input box. Once you have made your decision, hit
        the READY button and wait for all players to finish.
      </p>
      <p css={RulesPageStyles.section_paragraph}>
        During the Resolution Phase, all player decisions are revealed. On each
        card, you will see who selected that card, along with their bid. The
        star next to a player's name indicates they won the bid for the card.
        Dice rolls, results, and winning probabilities are displayed in the game
        log to the right.
      </p>
      <p css={RulesPageStyles.section_paragraph}>
        If you won the bid, the card will automatically highlight for you, and
        you can just hit Ready to obtain it. If you lost the bid, you need to
        select a Basic card to obtain. You may also opt to obtain a Basic card
        if you won the bid.
      </p>
      <TextWithSymbols
        css={RulesPageStyles.section_paragraph}
        content="The Tributes section will highlight Tributes whose conditions you will meet with your current selections. Note that this does not take into account any hidden information, such as other players' decisions or dice rolls!"
      />
      <TextWithSymbols
        css={RulesPageStyles.section_paragraph}
        content="While playing, you might find it useful to refer to the Player Summary View in the top right corner. This is a condensed version of each player's board. You can see each players' current $FAVOR, $GOLD, $MILITARY, and the breakdown of card types and affinities they own. You can click on the $FAVOR section to see detailed breakdowns of each player's $FAVOR sources. You can also click on the resources section to toggle a combined resource mode, where $GOLD plus $TOWN and $MILITARY plus $FORT are shown as singular totals. If you are uncertain about a section on the summary view, you can hover over it to see what it means."
      />
    </PageSection>
  );
  const war_probabilities_cells = range(0, 16).map((bonus) => {
    let css = [RulesPageStyles.probability_table_cell];
    let cells = range(1, 4).map((age) => {
      let p = probabilityWinningMultiBattle(age, bonus, [[age, 0]]);
      let pretty_p = Math.floor(Math.round(p * 100000) / 1000);

      return (
        <td key={age} css={css}>
          {bonus > 5 * age ? '' : pretty_p + '%'}
        </td>
      );
    });
    return (
      <tr key={bonus} css={RulesPageStyles.probability_table_row}>
        <td css={[css, RulesPageStyles.probability_table_cell_advantage]}>
          {bonus}
        </td>
        {cells}
      </tr>
    );
  });
  const war_probabilities_table = (
    <table css={RulesPageStyles.probability_table}>
      <thead>
        <tr>
          <th css={RulesPageStyles.probability_table_header}>Advantage</th>
          <th css={RulesPageStyles.probability_table_header}>Age I</th>
          <th css={RulesPageStyles.probability_table_header}>Age II</th>
          <th css={RulesPageStyles.probability_table_header}>Age III</th>
        </tr>
      </thead>
      <tbody>{war_probabilities_cells}</tbody>
    </table>
  );
  const war_probabilities_section = (
    <PageSection title="Clash Probabilities" linkName="war">
      <p css={RulesPageStyles.section_paragraph}>
        The table below shows the probability of winning a battle based on the
        age and the relative Military advantage.
      </p>
      <div css={RulesPageStyles.probability_table_container}>
        {war_probabilities_table}
      </div>
    </PageSection>
  );

  return (
    <>
      <div css={RulesPageStyles.container}>
        {overview_section}
        {resources_section}
        {/* {game_components_sections} */}
        {card_anatomy_section}
        {tribute_anatomy_section}
        {core_rules_section}
        {card_types_section}
        {card_subtypes_section}
        {other_cards_section}
        {basic_cards_section}
        {card_outputs_section}
        {war_probabilities_section}
        {playing_online_section}
        {gods_section}
      </div>
      {HoverCardStore.getHoverCardComponent()}
    </>
  );
}

function PageSection(props: {
  title?: string;
  linkName?: string;
  children: React.ReactNode;
  className?: string;
}) {
  let { title, linkName, children } = props;

  let anchor: React.ReactNode = null;
  let hash: React.ReactNode = null;
  if (linkName) {
    anchor = <a id={linkName} />;
    hash = (
      <a
        className="section-hash"
        css={RulesPageStyles2.hashLink}
        href={`#${linkName}`}
      >
        #
      </a>
    );
  }
  return (
    <div css={RulesPageStyles2.section} className={props.className}>
      {anchor}
      <div css={RulesPageStyles2.sectionTitle}>
        {title} {hash}
      </div>
      <div css={RulesPageStyles.section_content}>{children}</div>
    </div>
  );
}

const RulesPageStyles2 = {
  section: css({
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 'normal',

    '&:hover .section-hash': {
      visibility: 'visible',
    },
    breakInside: 'avoid',
  }),
  sectionTitle: css({
    textAlign: 'center',
    padding: 10,
    paddingBottom: 0,
    marginTop: 10,
    fontSize: 20,
  }),
  hashLink: css({
    color: UIColorPalette.rulesLinkHash,
    visibility: 'hidden',
  }),
};

const RulesPageStyles = {
  page: css({}),
  container: css({
    display: 'flex',
    flexDirection: 'column',

    width: 750,
    margin: 'auto',
    padding: 20,
    paddingTop: 0,

    backgroundColor: UIColorPalette.rulesBg,
    color: UIColorPalette.rulesText,

    '@media print': {
      backgroundColor: 'transparent',
    },
    [MEDIA_QUERY_MOBILE]: {
      width: '100%',
      boxSizing: 'border-box',
      padding: 10,
    },
  }),
  section_with_image: css({
    display: 'flex',
    flexDirection: 'row',
    padding: 20,

    [MEDIA_QUERY_MOBILE]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  }),
  section_with_image_mirrored: css({
    flexDirection: 'row-reverse',
    [MEDIA_QUERY_MOBILE]: {
      flexDirection: 'column',
    },
  }),
  section_with_image_narrow: css({
    marginLeft: 80,
    marginRight: 80,
    [MEDIA_QUERY_MOBILE]: {
      marginLeft: 0,
      marginRight: 0,
    },
  }),
  section_content: css({
    display: 'flex',
    flexDirection: 'column',
    fontWeight: '300',
    padding: 10,
    lineHeight: 1.3,
  }),
  section_paragraph: css({
    fontWeight: '300',
    margin: 10,
    lineHeight: 1.3,
  }),
  game_component_container: css({
    columnCount: 2,
    columnGap: 20,
  }),
  game_component: css({
    padding: 15,
    breakInside: 'avoid',
  }),
  section_title_no_padding: css({
    textAlign: 'center',
    marginTop: 10,
    fontSize: 20,
  }),
  imageCaption: css({
    fontStyle: 'italic',
    textAlign: 'center',
    fontSize: 10,
  }),
  card_anatomy_card: css({
    zoom: 3,
    [MEDIA_QUERY_MOBILE]: {
      zoom: 2,
    },
  }),
  image: css({
    paddingLeft: 10,
    paddingRight: 10,
  }),
  imageExamples: css({
    paddingLeft: 10,
    paddingRight: 10,
    margin: 'auto',
    display: 'block',
    height: 50,
  }),
  imagePhaseExample: css({
    padding: 10,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    height: 150,

    [MEDIA_QUERY_MOBILE]: {
      width: '90%',
      height: 'auto',
    },
  }),
  imageGameBoard: css({
    padding: 10,
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '90%',
  }),
  imagePlayerBoard: css({
    padding: 10,
    marginLeft: 'auto',
    marginRight: 'auto',
    width: 400,
    alignSelf: 'center',
  }),
  list: css({
    paddingLeft: 20,
  }),
  list_numbered: css({
    listStyleType: 'decimal',
    marginLeft: 20,
  }),
  listItemSymbolBullet: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',

    fontWeight: '300',

    padding: 5,
  }),
  symbol: css({
    width: 25,
    height: 25,

    padding: 10,
  }),
  symbol_inline: css({
    width: 16,
    height: 16,

    padding: 1,
    marginBottom: -3,
  }),
  subsection_title: css({
    fontWeight: 'bold',
    color: UIColorPalette.rulesLink,
  }),
  section_card_types_container: css({
    columnCount: 2,
    columnGap: 20,

    [MEDIA_QUERY_MOBILE]: {
      columnCount: 1,
    },
  }),
  section_basic_cards_container: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  }),
  section_card_types: css({
    display: 'flex',
    flexDirection: 'row',
    flexBasis: '50%',
    lineHeight: 1,
    padding: 10,
    gap: 5,
  }),
  section_basic_cards_description_container: css({
    display: 'flex',
    flexDirection: 'column',
  }),
  section_basic_cards_examples_container: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  }),
  section_card_outputs_container: css({
    marginTop: 10,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  }),
  section_card_outputs: css({
    minWidth: 80,
    marginRight: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    zoom: 1.5,
  }),
  card_with_note: css({}),
  deck_container: css({
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 4,
  }),
  deck_section: css({
    width: '100%',
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 5,
  }),
  decks_container: css({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
  }),
  probability_table_container: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  }),
  probability_table: css({
    color: UIColorPalette.rulesText,
    textAlign: 'center',
    border: '1px solid black',
    alignSelf: 'center',
  }),
  probability_table_cell: css({
    padding: 5,
    border: '1px solid black',
    width: '100px',
  }),
  probability_table_row: css({
    color: UIColorPalette.rulesText,

    ':nth-child(odd)': {
      backgroundColor: UIColorPalette.rulesTableBgDark,
      color: 'black',
    },
  }),
  probability_table_cell_advantage: css({
    width: '50px',
  }),
  probability_table_header: css({
    fontWeight: 'bold',
    border: '1px solid black',
    padding: '5px',
  }),
  textSymbol: css({
    height: 16,
    width: 'auto',
    verticalAlign: 'text-bottom',
    marginBottom: 1,
  }),
} as const;

export default RulesPage;
